import { supabase } from './supabase';
import type { StructuredAddress } from './storefront';

// ─────────────────────────────────────────────────────────────────────────────
// Customer portal client (per-business, Shopify-style login).
// Auth is passwordless: email → 6-digit code → bearer session token, stored in
// localStorage per business slug. All data flows through the portal-auth and
// portal-data edge functions; nothing is readable with the anon key directly.
// ─────────────────────────────────────────────────────────────────────────────

export interface PortalCustomer {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  /** Business/company name printed on invoices (B2B). */
  company?: string | null;
  wallet: number | null;
  default_shipping_address: StructuredAddress | null;
  default_billing_address: StructuredAddress | null;
  /** B2B terms: portal checkouts skip pay-at-checkout and are invoiced on delivery. */
  invoice_only?: boolean;
}

/** A saved address-book entry (Shopify-style, multiple per account). */
export interface PortalAddress {
  id: string;
  label: string | null;
  company: string | null;
  line1: string;
  line2: string | null;
  city: string | null;
  province: string | null;
  postal_code: string | null;
  country: string | null;
  phone: string | null;
  delivery_instructions: string | null;
  is_default_shipping: boolean;
  is_default_billing: boolean;
}

/** Address-book row → the structured shape used by checkout forms. */
export function portalAddressToStructured(a: PortalAddress): StructuredAddress {
  const structured: StructuredAddress = {
    company: a.company || '',
    line1: a.line1 || '',
    line2: a.line2 || '',
    city: a.city || '',
    province: a.province || '',
    postal_code: a.postal_code || '',
    country: a.country || 'Canada',
    formatted: '',
  };
  structured.formatted = [structured.line1, structured.line2, structured.city,
    [structured.province, structured.postal_code].filter(Boolean).join(' '), structured.country]
    .filter(Boolean).join(', ');
  return structured;
}

export interface PortalBusiness {
  id: string;
  name: string;
  slug: string;
  phone: string | null;
  email: string | null;
  logoUrl: string | null;
  storeEnabled: boolean;
  onlinePaymentsAvailable: boolean;
  /** Show the Wallet tab in the portal. */
  walletEnabled?: boolean;
  /** Customers may add wallet funds by card (always charged up front). */
  walletTopupEnabled?: boolean;
}

/** Bonus tier: top-ups of at least min_amount_cents earn bonus_cents extra. */
export interface WalletTopupPromotion {
  id: string;
  min_amount_cents: number;
  bonus_cents: number;
}

export interface PortalOrderItem {
  id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
}

export interface PortalOrder {
  id: string;
  order_number: string;
  status: 'new' | 'confirmed' | 'out_for_delivery' | 'delivered' | 'invoiced' | 'cancelled';
  source: string;
  fulfillment_type: 'delivery' | 'pickup';
  delivery_address: string | null;
  requested_date: string | null;
  requested_window: string | null;
  estimated_subtotal_cents: number;
  estimated_discount_cents: number;
  estimated_tax_cents: number;
  estimated_total_cents: number;
  customer_note: string | null;
  invoice_id: string | null;
  placed_at: string;
  items: PortalOrderItem[];
}

export interface PortalInvoice {
  id: string;
  invoice_number: string;
  status: string;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  total_cents: number;
  amount_paid_cents: number;
  amount_due_cents: number;
  finalized_at: string | null;
  due_at: string | null;
  created_at: string;
  pdf_url: string | null;
}

export interface PortalBooking {
  id: string;
  booking_number: number | null;
  title: string | null;
  status: string;
  start_date: string | null;
  start_time: string | null;
  end_date: string | null;
  total_amount: number | null;
  location: string | null;
  booking_address: string | null;
  mobile_booking: boolean | null;
}

export interface PortalWalletTransaction {
  id: string;
  amount: number;
  type: string;
  description: string | null;
  created_at: string;
}

/** Returnable containers a customer currently holds (e.g. water jugs). */
export interface PortalContainerBalance {
  stock_item_id: string;
  name: string;
  quantity: number;
  deposit_cents_held: number;
  /** Saved delivery address. Null when the containers are not tied to a site. */
  customer_address_id?: string | null;
  address_label?: string | null;
}

export interface PortalRental {
  id: string;
  quantity: number;
  returned_quantity: number;
  rate_cents: number;
  billing_interval: 'month' | 'week';
  status: 'pending' | 'active' | 'past_due' | 'canceled' | 'ended';
  cancel_at_period_end: boolean;
  started_at: string | null;
  next_bill_date: string | null;
  ended_at: string | null;
  created_at: string;
  item?: { id: string; name: string } | null;
}

export interface PortalOverview {
  business: PortalBusiness;
  profile: PortalCustomer;
  addresses: PortalAddress[];
  orders: PortalOrder[];
  invoices: PortalInvoice[];
  bookings: PortalBooking[];
  walletTransactions: PortalWalletTransaction[];
  walletPromotions?: WalletTopupPromotion[];
  containers?: PortalContainerBalance[];
  rentals?: PortalRental[];
}

// ── Session persistence ───────────────────────────────────────────────────────

interface StoredSession {
  token: string;
  customerId: string;
  /** Cached for instant avatar initials (e.g. embed account button). */
  displayName?: string;
}

/** Display name for avatars/greetings: "First Last", falling back to email. */
export function portalDisplayName(customer: {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}): string {
  return [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim() || customer.email || '';
}

export type PortalAccountKind = 'orders' | 'booking';

const sessionKey = (slug: string, kind: PortalAccountKind = 'orders') =>
  kind === 'booking' ? `nexus-booking-session:${slug}` : `nexus-portal-session:${slug}`;
const sessionChannelName = (slug: string, kind: PortalAccountKind = 'orders') =>
  kind === 'booking' ? `nexus-booking:${slug}` : `nexus-portal:${slug}`;

export function readPortalSession(slug: string, kind: PortalAccountKind = 'orders'): StoredSession | null {
  try {
    const raw = localStorage.getItem(sessionKey(slug, kind));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.token && parsed?.customerId ? parsed : null;
  } catch {
    return null;
  }
}

export function writePortalSession(slug: string, session: StoredSession | null, kind: PortalAccountKind = 'orders'): void {
  try {
    if (session) {
      localStorage.setItem(sessionKey(slug, kind), JSON.stringify(session));
    } else {
      localStorage.removeItem(sessionKey(slug, kind));
    }
    // Let other iframes/tabs (embeds, portal, store) react to login/logout.
    new BroadcastChannel(sessionChannelName(slug, kind)).postMessage({ type: 'session' });
  } catch {
    // storage unavailable — session just won't persist
  }
}

/**
 * Adopt a session handed off in the URL fragment (#pt=<token>&pc=<customerId>).
 * Embeds on external sites link to the hosted portal this way because browser
 * storage partitioning keeps their iframe login invisible to a top-level tab.
 * The fragment never reaches any server and is scrubbed from the URL here.
 */
export function adoptPortalHandoff(slug: string): void {
  try {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const token = params.get('pt');
    const customerId = params.get('pc');
    if (!token || !customerId) return;
    writePortalSession(slug, { token, customerId });
    params.delete('pt');
    params.delete('pc');
    const rest = params.toString();
    history.replaceState(null, '', window.location.pathname + window.location.search + (rest ? `#${rest}` : ''));
  } catch {
    // storage unavailable — the login form will show instead
  }
}

/** Subscribe to login/logout changes across iframes and tabs. */
export function subscribePortalSession(slug: string, onChange: () => void, kind: PortalAccountKind = 'orders'): () => void {
  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel(sessionChannelName(slug, kind));
    channel.onmessage = () => onChange();
  } catch {
    // BroadcastChannel unsupported — storage events still cover other tabs
  }
  const storageListener = (e: StorageEvent) => {
    if (e.key === sessionKey(slug, kind)) onChange();
  };
  window.addEventListener('storage', storageListener);
  return () => {
    channel?.close();
    window.removeEventListener('storage', storageListener);
  };
}

// ── Password strength ─────────────────────────────────────────────────────────

export interface PasswordStrength {
  /** 0 = too short, 1 = weak, 2 = fair, 3 = good, 4 = strong */
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  /** Tailwind-safe hex color for the meter/label. */
  color: string;
}

/** Score a candidate password for the signup/reset strength meter. */
export function scorePassword(password: string): PasswordStrength {
  if (password.length < 8) {
    return { score: 0, label: 'At least 8 characters', color: '#9ca3af' };
  }
  let points = 0;
  if (password.length >= 12) points++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) points++;
  if (/\d/.test(password)) points++;
  if (/[^a-zA-Z0-9]/.test(password)) points++;
  // Penalize passwords that are one repeated character or a trivial sequence.
  if (/^(.)\1+$/.test(password) || /^(0123|1234|2345|3456|4567|5678|6789|abcd|qwer|password)/i.test(password)) {
    points = Math.max(0, points - 2);
  }
  if (points <= 1) return { score: 1, label: 'Weak', color: '#ef4444' };
  if (points === 2) return { score: 2, label: 'Fair', color: '#f59e0b' };
  if (points === 3) return { score: 3, label: 'Good', color: '#10b981' };
  return { score: 4, label: 'Strong', color: '#059669' };
}

// ── API calls ─────────────────────────────────────────────────────────────────

async function invoke<T>(fn: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(fn, { body });
  if (error) {
    const context = (error as { context?: Response }).context;
    if (context) {
      try {
        const parsed = await context.json();
        if (parsed?.error === 'throttled' && parsed?.retryAfter) {
          throw new Error(`Please wait ${parsed.retryAfter}s before requesting another code`);
        }
        if (parsed?.error === 'unauthorized') throw new Error('unauthorized');
        if (parsed?.error) throw new Error(parsed.error);
      } catch (parseErr) {
        if (parseErr instanceof Error && (parseErr as Error).name !== 'SyntaxError') throw parseErr;
      }
    }
    throw new Error('Something went wrong — please try again');
  }
  if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
  return data as T;
}

export interface PortalAuthResult {
  token: string;
  customer: PortalCustomer;
  business: { id: string; name: string; slug: string };
}

/**
 * Email a 6-digit verification code. mode 'signup' fails fast when the email
 * already has a password account; mode 'reset' is used for password recovery.
 * Returns skipCode: true (and sends nothing) when staff exempted this
 * customer's email from verification — proceed without a code.
 */
export async function requestPortalCode(
  slug: string,
  email: string,
  mode?: 'signup' | 'reset',
  kind?: PortalAccountKind
): Promise<{ skipCode: boolean }> {
  const res = await invoke<{ ok: boolean; skipCode?: boolean }>(
    'portal-auth',
    { action: 'request', slug, email, ...(mode ? { mode } : {}), ...(kind === 'booking' ? { kind } : {}) }
  );
  return { skipCode: res?.skipCode === true };
}

/** Traditional email + password login. */
export async function portalPasswordLogin(
  slug: string,
  email: string,
  password: string,
  kind?: PortalAccountKind
): Promise<PortalAuthResult> {
  return invoke('portal-auth', { action: 'login', slug, email, password, ...(kind === 'booking' ? { kind } : {}) });
}

/** Create the account: code-verified email + chosen password. */
export async function portalSignup(
  slug: string,
  email: string,
  code: string,
  password: string,
  firstName?: string,
  lastName?: string,
  kind?: PortalAccountKind
): Promise<PortalAuthResult> {
  return invoke('portal-auth', {
    action: 'signup', slug, email, code, password, firstName, lastName,
    ...(kind === 'booking' ? { kind } : {}),
  });
}

/** Password recovery: code-verified email + new password (also signs in). */
export async function portalPasswordReset(
  slug: string,
  email: string,
  code: string,
  password: string,
  kind?: PortalAccountKind
): Promise<PortalAuthResult> {
  return invoke('portal-auth', { action: 'reset', slug, email, code, password, ...(kind === 'booking' ? { kind } : {}) });
}

/** One-time-code login (kept for accounts without a password). */
export async function verifyPortalCode(
  slug: string,
  email: string,
  code: string
): Promise<PortalAuthResult> {
  return invoke('portal-auth', { action: 'verify', slug, email, code });
}

export async function portalLogout(slug: string, kind: PortalAccountKind = 'orders'): Promise<void> {
  const session = readPortalSession(slug, kind);
  writePortalSession(slug, null, kind);
  if (session?.token) {
    try {
      await invoke('portal-auth', { action: 'logout', token: session.token });
    } catch {
      // best-effort server-side invalidation
    }
  }
}

export async function fetchPortalOverview(slug: string): Promise<PortalOverview> {
  const session = readPortalSession(slug);
  if (!session) throw new Error('unauthorized');
  return invoke('portal-data', { action: 'overview', slug, token: session.token });
}

/** Returns the /pay-invoice/:token token for an open invoice. */
export async function createPortalPayLink(slug: string, invoiceId: string): Promise<string> {
  const session = readPortalSession(slug);
  if (!session) throw new Error('unauthorized');
  const { token } = await invoke<{ token: string }>('portal-data', {
    action: 'pay_link',
    slug,
    token: session.token,
    invoiceId,
  });
  return token;
}

export async function updatePortalProfile(
  slug: string,
  fields: {
    firstName: string;
    lastName: string;
    phone: string;
    company?: string;
    address?: string;
    shippingAddress?: StructuredAddress | null;
    billingAddress?: StructuredAddress | null;
  }
): Promise<void> {
  const session = readPortalSession(slug);
  if (!session) throw new Error('unauthorized');
  await invoke('portal-data', { action: 'update_profile', slug, token: session.token, ...fields });
}

// ── Address book ─────────────────────────────────────────────────────────────

export interface SavePortalAddressInput {
  id?: string;
  label?: string;
  address: StructuredAddress;
  phone?: string;
  deliveryInstructions?: string;
  isDefaultShipping?: boolean;
  isDefaultBilling?: boolean;
}

export async function savePortalAddress(slug: string, input: SavePortalAddressInput): Promise<string> {
  const session = readPortalSession(slug);
  if (!session) throw new Error('unauthorized');
  const { id } = await invoke<{ id: string }>('portal-data', {
    action: 'save_address',
    slug,
    token: session.token,
    ...input,
  });
  return id;
}

export async function deletePortalAddress(slug: string, id: string): Promise<void> {
  const session = readPortalSession(slug);
  if (!session) throw new Error('unauthorized');
  await invoke('portal-data', { action: 'delete_address', slug, token: session.token, id });
}

/** Schedule (cancel=true) or revoke (cancel=false) a rental cancellation.
 *  The rental keeps running until the end of the paid period — no proration. */
export async function cancelPortalRental(
  slug: string,
  agreementId: string,
  cancel: boolean
): Promise<{ endsOn: string | null }> {
  const session = readPortalSession(slug);
  if (!session) throw new Error('unauthorized');
  const res = await invoke<{ ok: boolean; endsOn: string | null }>('portal-data', {
    action: 'cancel_rental',
    slug,
    token: session.token,
    agreementId,
    cancel,
  });
  return { endsOn: res.endsOn ?? null };
}

// ── Wallet top-ups ────────────────────────────────────────────────────────────

/** Returns the Stripe Checkout URL for a wallet top-up (always a card charge). */
export async function startWalletTopup(
  slug: string,
  amountCents: number,
  successUrl: string,
  cancelUrl: string
): Promise<string> {
  const session = readPortalSession(slug);
  if (!session) throw new Error('unauthorized');
  const { checkoutUrl } = await invoke<{ checkoutUrl: string }>('portal-data', {
    action: 'wallet_topup',
    slug,
    token: session.token,
    amountCents,
    successUrl,
    cancelUrl,
  });
  return checkoutUrl;
}

// ── Booking portal (separate login from the orders portal) ───────────────────

export interface BookingVehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  is_primary?: boolean | null;
}

export interface BookingReward {
  id: string;
  name: string;
  points_cost: number;
  kind: 'fixed_discount' | 'percent_discount' | 'wallet_credit';
  value_cents: number | null;
  percent_off: number | null;
}

export interface BookingHome {
  profile: PortalCustomer;
  vehicles: BookingVehicle[];
  invoices: Array<{
    id: string;
    invoice_number: string;
    status: string;
    total_cents: number;
    amount_due_cents: number;
    created_at: string;
  }>;
  bookings: Array<{
    id: string;
    booking_number: number | null;
    title: string | null;
    status: string;
    start_date: string | null;
    start_time: string | null;
    total_amount: number | null;
  }>;
  walletTransactions: PortalWalletTransaction[];
  points: number;
  rewards: BookingReward[];
  held: { id: string; redemption_id: string; name: string; points_cost: number; kind: string } | null;
  programActive: boolean;
}

function bookingToken(slug: string): string {
  const session = readPortalSession(slug, 'booking');
  if (!session) throw new Error('unauthorized');
  return session.token;
}

export function saveBookingSession(slug: string, result: PortalAuthResult): void {
  writePortalSession(slug, {
    token: result.token,
    customerId: result.customer.id,
    displayName: portalDisplayName(result.customer),
  }, 'booking');
}

export async function bookingEmailStatus(
  slug: string,
  email: string,
): Promise<'new' | 'account' | 'customer'> {
  const res = await invoke<{ state: 'new' | 'account' | 'customer' }>('portal-auth', {
    action: 'email_status',
    slug,
    email,
    kind: 'booking',
  });
  return res.state;
}

export async function bookingCheckoutSignup(
  slug: string,
  email: string,
  password: string,
  firstName: string,
  lastName: string,
): Promise<PortalAuthResult> {
  const result = await invoke<PortalAuthResult>('portal-auth', {
    action: 'checkout_signup',
    slug,
    email,
    password,
    firstName,
    lastName,
    kind: 'booking',
  });
  saveBookingSession(slug, result);
  return result;
}

export async function fetchBookingHome(slug: string): Promise<BookingHome> {
  return invoke('portal-data', { action: 'booking_home', slug, token: bookingToken(slug) });
}

export async function holdBookingReward(slug: string, redemptionId: string): Promise<void> {
  await invoke('portal-data', { action: 'hold_reward', slug, token: bookingToken(slug), redemptionId });
}

export async function redeemBookingWallet(slug: string, redemptionId: string): Promise<void> {
  await invoke('portal-data', { action: 'redeem_wallet', slug, token: bookingToken(slug), redemptionId });
}

export async function applyBookingReward(slug: string, bookingId: string, redemptionId: string): Promise<void> {
  await invoke('portal-data', {
    action: 'apply_booking_reward',
    slug,
    token: bookingToken(slug),
    bookingId,
    redemptionId,
  });
}

export async function releaseBookingReward(slug: string): Promise<void> {
  await invoke('portal-data', { action: 'release_reward', slug, token: bookingToken(slug) });
}
